"use client";

import { motion } from "framer-motion";
import { Hammer, Wrench, ShieldCheck, Rocket } from "lucide-react";
import { useLanguage } from "@/lib/i18n/context";
import { pageTheme } from "@/lib/theme/page-theme";
import type { ComponentType } from "react";

const milestoneIcons: ComponentType<{ className?: string }>[] = [
  Hammer, Wrench, ShieldCheck, Rocket, Rocket,
];

export function TimelineSection() {
  const { t } = useLanguage();
  const milestones = t.onas.timeline;

  return (
    <div className="py-10 md:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-stone-200 dark:bg-zinc-700/50 hidden md:block" />

          {milestones.map((milestone, idx) => {
            const Icon = milestoneIcons[idx] || Rocket;
            return (
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`relative mb-6 md:mb-7 flex flex-col md:flex-row items-center gap-5 md:gap-6 ${idx % 2 === 0 ? "md:flex-row-reverse" : ""
                  }`}
              >
                {/* Year Bubble */}
                <div className={pageTheme.timelineYearBubble}>
                  <span className="text-xs">{milestone.year}</span>
                </div>

                {/* Content Card */}
                <div className="w-full md:w-1/2 p-1 relative">
                  <div className={pageTheme.timelineCard}>
                    <div className="absolute top-0 left-0 w-1 h-full bg-orange-500 group-hover:w-2 transition-all" />

                    <div className="flex items-center gap-3 mb-2.5">
                      <div className={pageTheme.timelineIconBg}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <span className="text-orange-500 font-bold text-xs md:hidden">{milestone.year}</span>
                        <h4 className="text-base md:text-lg font-bold text-zinc-900 dark:text-white">{milestone.title}</h4>
                      </div>
                    </div>

                    <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed text-sm">
                      {milestone.description}
                    </p>
                  </div>
                </div>

                {/* Spacer for the other side on desktop */}
                <div className="hidden md:block w-1/2" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
